import { getActionError } from "../utils/actionFeedback";
import { useContext, useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Description } from "@headlessui/react";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";
import { DarkModeContext } from "../Context/DarkModeContext";
import axiosInstance from "../Context/axiosInstance";
import { buildCourseUpdatePayload, courseUrlFields, validateCourseUrls } from "../utils/coursePayload";

export default function CourseLinksModal({ open, onClose, courseId }) {
  const { isDarkMode } = useContext(DarkModeContext);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    setValues({});
    setFieldErrors({});
    axiosInstance.get(`/courses/${courseId}`, { signal: controller.signal })
      .then(({ data }) => { if (active) setValues(Object.fromEntries(courseUrlFields.map(([key]) => [key, data[key] || ""]))); })
      .catch(() => { if (active) setError("Could not load the course links. Please try again."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [open, courseId, retry]);

  const save = async (event) => {
    event.preventDefault();
    if (saving || loading) return;
    const errors = validateCourseUrls(values);
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await axiosInstance.get(`/courses/${courseId}`);
      const links = Object.fromEntries(courseUrlFields.map(([key]) => [key, values[key]?.trim() || null]));
      await axiosInstance.put(`/courses/${courseId}`, buildCourseUpdatePayload(data, links));
      toast.success("Course links updated.");
      onClose();
    } catch (err) {
      toast.error(getActionError(err, "Could not save course links."));
      setError(err.response?.data?.errors?.[0]?.reason || err.response?.data?.message || "Could not save the course links. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return <Dialog open={open} onClose={() => { if (!saving) onClose(); }} className="relative z-[60]">
    <div className="fixed inset-0 bg-slate-950/65 backdrop-blur-sm" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
      <DialogPanel className={`max-h-[calc(100dvh-2rem)] w-full max-w-xl overflow-y-auto rounded-2xl border p-6 shadow-2xl ${isDarkMode ? "border-white/10 bg-[#191919] text-white" : "border-slate-200 bg-white text-slate-900"}`}>
        <div className="flex items-center justify-between gap-4"><DialogTitle className="text-xl font-bold">Course links</DialogTitle><button type="button" onClick={onClose} disabled={saving} aria-label="Close course links" className="rounded-lg p-2 hover:bg-slate-500/10 disabled:opacity-50"><MdClose size={22} /></button></div>
        <Description className="mt-2 text-sm text-slate-500">Choose where the student Practice, Review My Mistakes, and My Performance cards open. Leave a field blank to use its default destination.</Description>
        {loading ? <p role="status" className="py-8 text-center">Loading course links...</p> : <form onSubmit={save} noValidate className="mt-5 space-y-4">
          {courseUrlFields.map(([key, label]) => <div key={key}>
            <label htmlFor={`course-link-${key}`} className="mb-2 block text-sm font-semibold">{label}</label>
            <input id={`course-link-${key}`} type="url" inputMode="url" value={values[key] || ""} onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))} disabled={saving || Object.keys(values).length === 0} aria-invalid={Boolean(fieldErrors[key])} aria-describedby={fieldErrors[key] ? `course-link-error-${key}` : undefined} placeholder="https://" className={`w-full rounded-xl border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-white/15 bg-white/5" : "border-slate-300 bg-white"}`} />
            {fieldErrors[key] && <p id={`course-link-error-${key}`} className="mt-1 text-sm text-red-500">{fieldErrors[key]}</p>}
          </div>)}
          {error && <p role="alert" className="text-sm text-red-500">{error}{!Object.keys(values).length && <button type="button" onClick={() => setRetry((value) => value + 1)} className="ml-2 underline">Retry</button>}</p>}
          <div className="flex justify-end gap-3 pt-3"><button type="button" disabled={saving} onClick={onClose} className="rounded-xl border border-slate-400/30 px-4 py-3 text-sm">Cancel</button><button type="submit" disabled={saving || Object.keys(values).length === 0} className="rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{saving ? "Saving" : "Save"}</button></div>
        </form>}
      </DialogPanel>
    </div>
  </Dialog>;
}
